/**
 * Extracted UI components for TeamFormSlideout:
 *   - MemberRow / MembersSection  (member picker)
 *   - TeamFormContent             (slideout body rendered inside FormProvider)
 */

import type { Membership } from 'api-client';
import { memo, useCallback, useMemo, useState } from 'react';

import { useOrgMembers } from '@/hooks/useOrgMembers';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import { Input } from '@/lib/base/forms/input/input';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';

// =============================================================================
// MemberRow
// =============================================================================

interface MemberRowProps {
  member: Membership;
  isSelected: boolean;
  onToggle: (entityId: string) => void;
}

function getInitials(member: Membership): string {
  const first = member.firstName[0] ?? '';
  const last = member.lastName[0] ?? '';
  const combined = (first + last).toUpperCase();
  return combined || (member.email[0]?.toUpperCase() ?? '?');
}

function MemberRowImpl({ member, isSelected, onToggle }: MemberRowProps) {
  const handleToggle = useCallback(() => {
    onToggle(member.entityId);
  }, [onToggle, member.entityId]);

  const initials = getInitials(member);

  return (
    <li className='flex items-center gap-3 py-2'>
      <Checkbox
        isSelected={isSelected}
        onChange={handleToggle}
        aria-label={`Select ${member.displayName}`}
      />
      <button
        type='button'
        className='flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left'
        onClick={handleToggle}
        tabIndex={-1}
      >
        <Avatar size='sm' initials={initials} />
        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-medium text-[var(--color-text-primary)]'>
            {member.displayName}
          </p>
          <p className='truncate text-xs text-[var(--color-text-tertiary)]'>
            {member.email}
          </p>
        </div>
      </button>
    </li>
  );
}

const MemberRow = memo(MemberRowImpl);

// =============================================================================
// MembersSection — member picker embedded in the form
// =============================================================================

export interface MembersSectionProps {
  orgId: number;
  selectedUserIds: Set<string>;
  onToggle: (entityId: string) => void;
  isSaving: boolean;
  initialMemberIdsError: boolean;
}

export function MembersSection({
  orgId,
  selectedUserIds,
  onToggle,
  isSaving,
  initialMemberIdsError,
}: MembersSectionProps) {
  const { members, isLoading, isError, refetch } = useOrgMembers(orgId);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) {
      return members;
    }
    const query = searchQuery.toLowerCase();
    return members.filter(
      m =>
        m.displayName.toLowerCase().includes(query) ||
        m.email.toLowerCase().includes(query)
    );
  }, [members, searchQuery]);

  if (initialMemberIdsError) {
    return (
      <p className='py-4 text-sm text-[var(--color-text-secondary)]'>
        We couldn't load the current members. Close and reopen to retry.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingIndicator size='sm' />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-col items-center gap-3 py-6'>
        <p className='text-sm text-[var(--color-text-secondary)]'>
          We couldn't load the members.
        </p>
        <button
          type='button'
          className='text-sm font-medium text-fg-brand-primary hover:underline'
          onClick={handleRetry}
          disabled={isSaving}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <Input
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder='Search members'
        aria-label='Search members'
        isDisabled={isSaving}
      />

      <p className='text-xs text-[var(--color-text-tertiary)]'>
        {selectedUserIds.size} of {members.length} selected
      </p>

      {filteredMembers.length === 0 ? (
        <p className='py-2 text-sm text-[var(--color-text-tertiary)]'>
          No members found.
        </p>
      ) : (
        <ul className='flex flex-col divide-y divide-[var(--color-border-secondary)]'>
          {filteredMembers.map(member => (
            <MemberRow
              key={member.entityId}
              member={member}
              isSelected={selectedUserIds.has(member.entityId)}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </>
  );
}

// =============================================================================
// TeamFormContent — inner component that accesses FormContext
//
// Separated from the outer component so it can call useFormContext() as a
// descendant of FormProvider. Renders the slideout body and footer.
// =============================================================================

export interface TeamFormContentProps {
  title: string;
  submitLabel: string;
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  isSaving: boolean;
  onClose: () => void;
  onSave: (setSubmitError: (msg: string) => void) => Promise<void>;
  orgId: number;
  selectedUserIds: Set<string>;
  onToggleMember: (entityId: string) => void;
  isMemberIdsLoading: boolean;
  initialMemberIdsError: boolean;
}

export function TeamFormContent({
  title,
  submitLabel,
  displayName,
  onDisplayNameChange,
  description,
  onDescriptionChange,
  isSaving,
  onClose,
  onSave,
  orgId,
  selectedUserIds,
  onToggleMember,
  isMemberIdsLoading,
  initialMemberIdsError,
}: TeamFormContentProps) {
  const { setSubmitError } = useFormContext();

  const handleSubmit = useCallback(async () => {
    await onSave(setSubmitError);
  }, [onSave, setSubmitError]);

  // Block Save until the current-member baseline has loaded successfully.
  // Saving before the baseline resolves (or after it fails) would diff the
  // selection against an empty/stale baseline and send association mutations
  // for the wrong members.
  const isMemberBaselineBlocked = isMemberIdsLoading || initialMemberIdsError;
  let saveDisabledTooltip: string | undefined;
  if (isMemberIdsLoading) {
    saveDisabledTooltip = 'Loading current members…';
  } else if (initialMemberIdsError) {
    saveDisabledTooltip =
      'Current members could not be loaded. Close and reopen to retry.';
  }

  return (
    <>
      <SlideoutMenu.Header onClose={onClose}>
        <h2 className='text-lg font-semibold text-text-primary'>{title}</h2>
      </SlideoutMenu.Header>

      <SlideoutMenu.Content>
        <div className='space-y-4 py-4'>
          <FormField name='displayName' label='Team name' isRequired>
            {({ isInvalid, showError, inputId }) => (
              <InputGroup
                aria-label='Team name'
                value={displayName}
                onChange={onDisplayNameChange}
                isDisabled={isSaving}
                isInvalid={isInvalid}
                showError={showError}
              >
                <InputBase id={inputId} placeholder='e.g. Sales' />
              </InputGroup>
            )}
          </FormField>

          <FormField name='description' label='Description'>
            {({ isInvalid, showError, inputId }) => (
              <InputGroup
                aria-label='Description'
                value={description}
                onChange={onDescriptionChange}
                isDisabled={isSaving}
                isInvalid={isInvalid}
                showError={showError}
              >
                <InputBase
                  id={inputId}
                  placeholder='Describe this team (optional)'
                />
              </InputGroup>
            )}
          </FormField>

          {/* Members section */}
          <div className='space-y-3 pt-2'>
            <h3 className='text-sm font-medium text-[var(--color-text-primary)]'>
              Members
            </h3>
            {isMemberIdsLoading ? (
              <div className='flex items-center justify-center py-6'>
                <LoadingIndicator size='sm' />
              </div>
            ) : (
              <MembersSection
                orgId={orgId}
                selectedUserIds={selectedUserIds}
                onToggle={onToggleMember}
                isSaving={isSaving}
                initialMemberIdsError={initialMemberIdsError}
              />
            )}
          </div>
        </div>
      </SlideoutMenu.Content>

      <SlideoutMenu.Footer>
        <div className='flex justify-end gap-3'>
          <Button
            color='secondary'
            size='sm'
            onClick={onClose}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <SubmitButton
            size='sm'
            color='primary'
            onClick={handleSubmit}
            isDisabled={isSaving || isMemberBaselineBlocked}
            disabledTooltip={saveDisabledTooltip}
          >
            {isSaving ? 'Saving…' : submitLabel}
          </SubmitButton>
        </div>
      </SlideoutMenu.Footer>
    </>
  );
}

// Re-export FormProvider so TeamFormSlideout doesn't need a separate import.
export { FormProvider };
